import { JSX, ReactNode } from 'react';

import MUIGrid, {
  GridProps as MUIGridProps,
  GridDirection,
  GridWrap,
} from '@mui/material/Grid';

import {
  Spacing,
  GridContentAlignment,
  GridItemsAlignment,
  GridJustification,
} from './Grid.types';
import { GridContainer } from './GridContainer';

/**
 * Размер сеточного элемента для различных размеров экранов.
 */
export type GridSize =
  | 'auto'
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12;

/**
 * Свойства компонента {@link Grid}
 */
export type GridProps = JSX.IntrinsicAttributes & {
  /**
   * Пространство между элементами сетки.
   */
  spacing?: Spacing;

  /**
   * Определяет, является ли компонент Grid контейнером.
   *
   * @defaultValue `false`
   */
  container?: boolean;

  /**
   * Выравнивание содержимого по кросс-оси для контейнеров сетки.
   */
  alignContent?: GridContentAlignment;

  /**
   * Выравнивание элементов сетки по кросс-оси для контейнеров сетки.
   */
  alignItems?: GridItemsAlignment;

  /**
   * Дочерние элементы компонента Grid.
   */
  children?: ReactNode;

  /**
   * Определяет, является ли компонент Grid элементом сетки.
   *
   * @defaultValue `false`
   */
  item?: boolean;

  /**
   * Направление контейнера сетки.
   */
  direction?: GridDirection;

  /**
   * Выравнивание содержимого по главной оси для контейнеров сетки.
   */
  justifyContent?: GridJustification;

  /**
   * Размер сеточного элемента для больших экранов.
   */
  lg?: boolean | GridSize;

  /**
   * Размер сеточного элемента для средних экранов.
   */
  md?: boolean | GridSize;

  /**
   * Размер сеточного элемента для маленьких экранов.
   */
  sm?: boolean | GridSize;

  /**
   * Свойство flex wrap для контейнеров сетки.
   */
  wrap?: GridWrap;

  /**
   * Размер сеточного элемента для очень больших экранов.
   */
  xl?: boolean | GridSize;

  /**
   * Размер сеточного элемента для очень маленьких экранов.
   */
  xs?: boolean | GridSize;

  /**
   * Если true, элемент будет иметь нулевую минимальную ширину.
   *
   * @defaultValue `false`
   */
  zeroMinWidth?: boolean;

  /**
   * Пространство между колонками сетки.
   */
  columnSpacing?: Spacing;
} & MUIGridProps;

/**
 * Компонент для рендеринга сеточного макета, который может использоваться как контейнер или как элемент сетки.
 *
 * @public
 * @param spacing - Пространство между элементами сетки.
 * @param container - Определяет, является ли компонент Grid контейнером.
 * @param props - Остальные свойства компонента {@link GridProps}
 * @returns  JSX элемент, представляющий сеточный макет.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link GridProps}
 */
export const Grid = ({ spacing, container, ...props }: GridProps) => {
  if (container) return <GridContainer {...props} spacing={spacing} />;

  return <MUIGrid {...props} />;
};
